import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
	cloneElement,
	type HTMLAttributes,
	type ReactElement,
	useState
} from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { z } from 'zod'
import { InputText } from '@/components/commons/input-text'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogTitle,
	DialogTrigger
} from '@/components/ui/dialog'
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger
} from '@/components/ui/drawer'
import { Spinner } from '@/components/ui/spinner'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import { errorHandler } from '@/helpers/utils'
import { addWalkIn } from '@/sdk'
import { type TKey, useTranslation } from '@/translations/i18n'

const WalkInSchema = z.object({
	name: z.string().trim().min(1, 'marquee:error_walk_in_name_required'),
	airbnbHostId: z.string().trim().optional(),
	email: z.string().trim().optional(),
	salesforceAccountId: z.string().trim().optional()
})

type WalkInValues = z.infer<typeof WalkInSchema>

const EMPTY: WalkInValues = {
	name: '',
	airbnbHostId: '',
	email: '',
	salesforceAccountId: ''
}

interface AddWalkInDialogProps {
	eventId?: string
	trigger: ReactElement<HTMLAttributes<HTMLElement>>
}

/**
 * "Add walk-in" form. Creates the walk-in (name/email) and, when a host ID is
 * provided, assigns it in a follow-up call. Desktop → `Dialog`, mobile →
 * `Drawer`; the form body is shared between the two.
 */
function AddWalkInDialog({ eventId, trigger }: AddWalkInDialogProps) {
	const { t } = useTranslation()
	const isDesktop = useMinWidth('l')
	const [open, setOpen] = useState(false)

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors }
	} = useForm<WalkInValues>({
		resolver: zodResolver(WalkInSchema),
		defaultValues: EMPTY
	})

	const close = () => {
		setOpen(false)
		reset(EMPTY)
	}

	const queryClient = useQueryClient()

	/**
	 * One POST carries the host ID too, when the desk captured it (the backend
	 * derives status `unresolved`, or `missing_host_id` without it). Routing it
	 * through /assign-host afterwards would be a second round-trip at the busiest
	 * moment of the event, for the same stored result; that endpoint now answers
	 * during a live event, so it is a choice rather than the only way round.
	 */
	const add = useMutation({
		mutationFn: async (values: WalkInValues) => {
			const response = await addWalkIn({
				path: { eventId: eventId ?? '' },
				body: {
					name: values.name.trim(),
					email: values.email?.trim() || undefined,
					airbnb_host_id: values.airbnbHostId?.trim() || undefined,
					salesforce_account_id:
						values.salesforceAccountId?.trim() || undefined
				}
			})
			return response.data
		},
		onSuccess: async (attendee) => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.events.detail(eventId ?? '')
			})
			toast.success(
				t('marquee:checkin_walk_in_added', { name: attendee.name })
			)
			close()
		},
		onError: (error) => errorHandler(error)
	})

	const onSubmit = handleSubmit((values) => {
		if (!eventId) {
			return
		}
		add.mutate(values)
	})

	const errorText = (key?: string) => (key ? t(key as TKey) : undefined)

	const fields = (
		<div className='flex flex-col gap-5'>
			<InputText
				errorMessage={errorText(errors.name?.message)}
				label={t('marquee:checkin_walk_in_name_label')}
				placeholder={t('marquee:checkin_walk_in_name_placeholder')}
				type='text'
				{...register('name')}
			/>
			<InputText
				helpText={t('marquee:checkin_walk_in_host_id_hint')}
				label={t('marquee:checkin_walk_in_host_id_label')}
				placeholder={t('marquee:checkin_walk_in_host_id_placeholder')}
				type='text'
				{...register('airbnbHostId')}
			/>
			<InputText
				helpText={t(
					'marquee:checkin_walk_in_salesforce_account_id_hint'
				)}
				label={t('marquee:checkin_walk_in_salesforce_account_id_label')}
				placeholder={t(
					'marquee:checkin_walk_in_salesforce_account_id_placeholder'
				)}
				type='text'
				{...register('salesforceAccountId')}
			/>
			<InputText
				helpText={t('marquee:checkin_walk_in_email_hint')}
				label={t('marquee:checkin_walk_in_email_label')}
				placeholder={t('marquee:checkin_walk_in_email_placeholder')}
				type='email'
				{...register('email')}
			/>
		</div>
	)

	const actions = (
		<>
			<Button onClick={close} type='button' variant='tertiary'>
				{t('marquee:checkin_walk_in_back')}
			</Button>
			<Button
				disabled={add.isPending}
				onClick={onSubmit}
				type='button'
				variant='primary'
			>
				{t('marquee:checkin_add_walk_in')}
				{add.isPending && <Spinner size={16} />}
			</Button>
		</>
	)

	if (isDesktop) {
		return (
			<Dialog
				onOpenChange={(next) => (next ? setOpen(true) : close())}
				open={open}
			>
				<DialogTrigger render={trigger} />
				<DialogContent className='max-w-120 grid-rows-[minmax(0,1fr)_auto]'>
					<DialogBody className='gap-4'>
						<div className='flex flex-col gap-1'>
							<DialogTitle>
								{t('marquee:checkin_walk_in_title')}
							</DialogTitle>
							<DialogDescription className='text-neutral-500'>
								{t('marquee:checkin_walk_in_subtitle')}
							</DialogDescription>
						</div>
						{fields}
					</DialogBody>
					<DialogFooter className='flex flex-row justify-between'>
						{actions}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<Drawer
			onOpenChange={(next) => (next ? setOpen(true) : close())}
			open={open}
		>
			<DrawerTrigger asChild>
				{cloneElement(trigger, {
					onPointerDown: (e: React.PointerEvent) => e.preventDefault()
				})}
			</DrawerTrigger>
			<DrawerContent>
				<DrawerHeader className='pb-4'>
					<DrawerTitle className='font-semibold text-neutral-900 text-xl'>
						{t('marquee:checkin_walk_in_title')}
					</DrawerTitle>
					<DrawerDescription className='text-neutral-500'>
						{t('marquee:checkin_walk_in_subtitle')}
					</DrawerDescription>
				</DrawerHeader>
				<div className='min-h-0 flex-1 overflow-y-auto px-4'>
					{fields}
				</div>
				<DrawerFooter className='flex-row justify-between'>
					{actions}
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	)
}

export { AddWalkInDialog }
